/**
 * How to play, in three slides per game.
 *
 * Shown the first time someone opens a game on their phone, and on request after
 * that. Three slides because that is about as long as anyone at a party will read
 * before the host starts the game anyway: what you do, how you score, one tip.
 */

import type { GameId } from './common.ts';

export interface HowToSlide {
  emoji: string;
  title: string;
  body: string;
}

export const HOW_TO_PLAY: Record<GameId, HowToSlide[]> = {
  movie: [
    {
      emoji: '🎬',
      title: 'Link the films',
      body: 'On your turn, name a film that shares someone with the last one: an actor, the director, the writer, the composer or the cinematographer.',
    },
    {
      emoji: '⏱️',
      title: 'Beat the clock',
      body: 'Run out of time, or give up, and you lose the round. Everybody else survives. The chain just keeps growing until somebody cracks.',
    },
    {
      emoji: '🔎',
      title: 'Stuck?',
      body: 'Tap a film to look through its cast. Each person can only carry so many links, so the obvious stars run out.',
    },
  ],
  quiz: [
    {
      emoji: '🎯',
      title: 'Answer fast',
      body: 'Everyone gets the same question. Right answers score, and the quickest right answers score most.',
    },
    {
      emoji: '🔥',
      title: 'Keep a streak',
      body: 'Answers in a row build a streak that multiplies your points. One wrong answer resets it.',
    },
    {
      emoji: '🗂️',
      title: 'Pick the packs',
      body: 'The host chooses the subjects, the length and whether answers are multiple choice or typed.',
    },
  ],
  quiplash: [
    {
      emoji: '✍️',
      title: 'Write something funny',
      body: 'You get two prompts. Answer both. Somebody else has each prompt too.',
    },
    {
      emoji: '⚔️',
      title: 'Head to head',
      body: 'Answers go up two at a time and the room votes for the funnier one. You never vote on your own.',
    },
    {
      emoji: '💥',
      title: 'Quiplash!',
      body: 'Win every single vote in a matchup for a big bonus. The final round is worth double.',
    },
  ],
  fibbage: [
    {
      emoji: '🤥',
      title: 'Make up an answer',
      body: 'A true but strange fact goes up with a blank in it. Write a fake answer convincing enough to fool somebody.',
    },
    {
      emoji: '🕵️',
      title: 'Find the truth',
      body: 'Every lie and the real answer go up together. Pick the true one. You cannot pick your own lie.',
    },
    {
      emoji: '🏆',
      title: 'Scoring',
      body: 'Points for finding the truth, and points for every person your lie fooled. Fool everybody for a Super Lie.',
    },
  ],
  enough: [
    {
      emoji: '🧠',
      title: 'About somebody',
      body: 'Each round, one person answers a question about themselves honestly. Everybody else writes an answer they might have given.',
    },
    {
      emoji: '🔍',
      title: 'Spot the real one',
      body: 'All the answers go up. Everyone except that person picks the one they think is true.',
    },
    {
      emoji: '🎭',
      title: 'Scoring',
      body: 'Points for spotting the truth, and for every person your fake fooled. The person being asked scores when people know them.',
    },
  ],
  wavelengths: [
    {
      emoji: '📡',
      title: 'One clue',
      body: 'One player sees a hidden point on a bar between two opposites, like Cold and Hot. They give a single clue that sits at that point.',
    },
    {
      emoji: '🎚️',
      title: 'Slide to it',
      body: 'Everyone else slides to where they think the point is. Closer scores more, and dead on scores most.',
    },
    {
      emoji: '💡',
      title: 'Good clues',
      body: 'A thing, never a number or a direction. "Soup" works for Cold to Hot. "Seven" does not.',
    },
  ],
  obscurity: [
    {
      emoji: '🔎',
      title: 'Same topic',
      body: 'Everyone gets the same topic, like "name a dog breed". Type one answer.',
    },
    {
      emoji: '🦄',
      title: 'Rare is good',
      body: 'The fewer people who would think of your answer, the more it scores. The obvious answers score least, the deep cuts score most.',
    },
    {
      emoji: '⚠️',
      title: 'Not too far',
      body: 'An answer we cannot place scores nothing. Reach, but make sure it is real.',
    },
  ],
  superlatives: [
    {
      emoji: '🏆',
      title: 'Who here is most likely to…',
      body: 'Everyone gets the same question about the room. Pick somebody. Never yourself.',
    },
    {
      emoji: '🗳️',
      title: 'The count',
      body: 'Votes go up one name at a time, fewest first, ending on whoever the room chose.',
    },
    {
      emoji: '🤝',
      title: 'Scoring',
      body: 'Being chosen scores, and so does choosing whoever the room chose. It is about situations, never a verdict on anybody.',
    },
  ],
  chameleon: [
    {
      emoji: '🦎',
      title: 'One of you is the chameleon',
      body: 'Sixteen words go up. Everyone sees the secret one highlighted except the chameleon, who has to bluff.',
    },
    {
      emoji: '💬',
      title: 'One word each',
      body: 'Give a clue that proves you know the word without handing it to the chameleon. Too vague looks suspicious.',
    },
    {
      emoji: '🗳️',
      title: 'Point the finger',
      body: 'Vote out the chameleon. Caught, they get one guess at the word to escape. Voting for them scores either way.',
    },
  ],
  hottakes: [
    {
      emoji: '🌶️',
      title: 'Rate the take',
      body: 'Everyone gets the same statement. Say how much you agree, from 1 to 10. Nobody sees it until the reveal.',
    },
    {
      emoji: '🎯',
      title: 'Read the room',
      body: "Then guess the room's average. Within half a point is full marks. Every third round, guess one player's rating instead.",
    },
    {
      emoji: '🔥',
      title: 'The hottest take',
      body: 'The reveal lines every rating up. Whoever is furthest from everyone else had the hottest take.',
    },
  ],
  higherlower: [
    {
      emoji: '📅',
      title: 'Which came first?',
      body: 'Two films. Tap the one that came out first. Get it right and the next film slides in.',
    },
    {
      emoji: '🔥',
      title: 'Keep the run going',
      body: 'One wrong answer, or fifteen seconds without one, ends your run. Your best run is kept.',
    },
    {
      emoji: '⚔️',
      title: 'Together',
      body: 'Playing with others, everyone gets the same films in the same order. The longest run wins; on a tie, the quicker one.',
    },
  ],
  castreveal: [
    {
      emoji: '🎭',
      title: 'Who is in it?',
      body: 'Cast names appear one at a time, the least known first. Name the film as soon as you can.',
    },
    {
      emoji: '💯',
      title: 'Quicker is better',
      body: 'A right answer with one name showing scores 600; each new name takes 100 off. The director is the final clue.',
    },
    {
      emoji: '🔒',
      title: 'Careful',
      body: 'A wrong guess locks you out for three seconds. Against others, the first right answer takes the round.',
    },
  ],
  posterzoom: [
    {
      emoji: '🔍',
      title: 'What is the poster?',
      body: 'A film poster starts zoomed right in and blurred, and slowly pulls back. Name the film.',
    },
    {
      emoji: '💯',
      title: 'Quicker is better',
      body: 'The sooner you name it, the more it scores: up to 600.',
    },
    {
      emoji: '🔒',
      title: 'Careful',
      body: 'A wrong guess locks you out for three seconds. Against others, the first right answer takes the round.',
    },
  ],
  boxart: [
    {
      emoji: '🎮',
      title: 'What is the game?',
      body: 'A video game’s box art starts zoomed right in and blurred, and slowly pulls back. Type the game and pick it from the list.',
    },
    {
      emoji: '💯',
      title: 'Quicker is better',
      body: 'The sooner you name it, the more it scores: up to 600. A remake with the same name counts too.',
    },
    {
      emoji: '🔒',
      title: 'Careful',
      body: 'A wrong guess locks you out for three seconds. Against others, the first right answer takes the round.',
    },
  ],
  sixdegrees: [
    {
      emoji: '🔗',
      title: 'Link two films',
      body: 'Get from the first film to the second by naming films that share someone: an actor, the director, the writer and so on.',
    },
    {
      emoji: '🏁',
      title: 'Race',
      body: 'Everyone links the same pair, each on their own chain. The first to connect wins.',
    },
    {
      emoji: '⛳',
      title: 'Par',
      body: 'Fewer films scores more: par is a thousand, and each film over costs a hundred.',
    },
  ],
  connect4: [
    {
      emoji: '🔴',
      title: 'Drop a counter',
      body: 'Take turns dropping a counter into a column. It falls to the lowest space.',
    },
    {
      emoji: '➡️',
      title: 'Four in a row',
      body: 'Get four in a line, across, up or diagonally, to win the game.',
    },
    {
      emoji: '🏆',
      title: 'Match',
      body: 'Win the most games in the match. The starter swaps every game.',
    },
  ],
  noughts: [
    {
      emoji: '❌',
      title: 'Three in a row',
      body: 'Take turns marking a square. Three in a line wins.',
    },
    {
      emoji: '🧩',
      title: 'Ultimate',
      body: 'Nine boards in one. The square you play sends your opponent to the matching board. Win small boards to make a line on the big one.',
    },
    {
      emoji: '🏆',
      title: 'Match',
      body: 'Win the most games in the match. The starter swaps every game.',
    },
  ],
  battleships: [
    {
      emoji: '🚢',
      title: 'Hide your fleet',
      body: 'Place your five ships on your grid, or tap Random. Your opponent never sees them.',
    },
    {
      emoji: '🎯',
      title: 'Take turns firing',
      body: 'Tap a square on their grid. Hit, miss or sunk, you will know straight away.',
    },
    {
      emoji: '💥',
      title: 'Sink them all',
      body: 'The first to sink every ship in the other fleet wins.',
    },
  ],
  dots: [
    {
      emoji: '➖',
      title: 'Draw a line',
      body: 'Take turns drawing a line between two neighbouring dots.',
    },
    {
      emoji: '📦',
      title: 'Close a box',
      body: 'Draw the fourth side of a box and it is yours, and you go again.',
    },
    {
      emoji: '🏆',
      title: 'Most boxes wins',
      body: 'When every line is drawn, the player with more boxes wins. Try not to hand over the third side.',
    },
  ],
  wordduel: [
    {
      emoji: '🔤',
      title: 'Guess the word',
      body: 'A five-letter word, six tries. Type a word and press Enter.',
    },
    {
      emoji: '🟩',
      title: 'Read the colours',
      body: 'Green: right letter, right place. Yellow: in the word, somewhere else. Grey: not in it.',
    },
    {
      emoji: '⚔️',
      title: 'Together',
      body: 'Everyone has the same word and sees each other’s colours, never the letters. Fewest guesses takes the word.',
    },
  ],
  anagrams: [
    {
      emoji: '🔠',
      title: 'Seven letters',
      body: 'Make as many words as you can, three letters or more, using each letter once.',
    },
    {
      emoji: '⏱️',
      title: 'Ninety seconds',
      body: 'Longer words score more, and using all seven letters scores a thousand.',
    },
    {
      emoji: '⚔️',
      title: 'Together',
      body: 'A word only you found counts double. At the end, see the words nobody spotted.',
    },
  ],
  poker: [
    {
      emoji: '🃏',
      title: 'Two cards each',
      body: 'Your two cards are on your phone only. Five more are shared in the middle, three, then one, then one.',
    },
    {
      emoji: '💰',
      title: 'Bet, call or fold',
      body: 'Check when nothing is owed, call to match a bet, raise to push, or fold to get out. Blinds start each hand.',
    },
    {
      emoji: '🏆',
      title: 'Best five cards',
      body: 'At the showdown the best five cards from your two and the middle win the pot. Most chips at the end wins the game.',
    },
  ],
  liarsdice: [
    {
      emoji: '🎲',
      title: 'Five hidden dice',
      body: 'Everyone rolls in secret. Only you can see your own dice.',
    },
    {
      emoji: '📣',
      title: 'Bid higher',
      body: 'Bid how many of a face are on the whole table: "four fives". Each bid must beat the last: more dice, or the same number of a higher face.',
    },
    {
      emoji: '🤥',
      title: 'Liar!',
      body: 'Think the last bid is a lie? Call it. The dice come out, and whoever was wrong loses a die. With ones wild, ones count as anything.',
    },
  ],
  whosaid: [
    {
      emoji: '✍️',
      title: 'Answer honestly',
      body: 'Everyone answers the same question about themselves. Nobody sees who wrote what.',
    },
    {
      emoji: '🕵️',
      title: 'Match them all',
      body: 'Every answer comes up at once. Put a name to each one, then lock in. Each person wrote one.',
    },
    {
      emoji: '🎭',
      title: 'Fool them',
      body: 'A right guess scores 100. Each person who guesses wrong on your answer scores you 50.',
    },
  ],
  wouldyourather: [
    {
      emoji: '🤔',
      title: 'Pick a side',
      body: 'Each round is a dilemma. Choose which you would rather, in secret.',
    },
    {
      emoji: '🔢',
      title: 'Call the split',
      body: 'Say how many of the room, you included, will pick the first one.',
    },
    {
      emoji: '🎯',
      title: 'Read the room',
      body: 'Exactly right scores 300, one out 150, two out 50. Then see who went where.',
    },
  ],
  cheat: [
    {
      emoji: '🃏',
      title: 'Get rid of your cards',
      body: 'The whole deck is dealt out. First to play their last card, and get away with it, wins.',
    },
    {
      emoji: '🤫',
      title: 'Claim the rank',
      body: 'On your turn put one to four cards face down and say they are the rank called: aces, then twos, threes and on to kings. Lie if you have to.',
    },
    {
      emoji: '🚨',
      title: 'Cheat!',
      body: 'After every play anyone can call Cheat. The cards are turned over: a liar picks up the whole pile, and so does a wrong accuser.',
    },
  ],
  rankem: [
    {
      emoji: '🏅',
      title: 'Judge and jury',
      body: 'Each round one player is the judge and puts five things in order, in secret.',
    },
    {
      emoji: '🔮',
      title: 'Read their mind',
      body: 'Everybody else puts the same five in the order they think the judge did.',
    },
    {
      emoji: '💯',
      title: 'Scores',
      body: '100 for each thing in exactly the judge’s spot, 40 for one place out. The judge scores when people match their number one.',
    },
  ],
  spotlight: [
    {
      emoji: '🔦',
      title: 'In the spotlight',
      body: 'One player at a time answers a question about themselves, in secret.',
    },
    {
      emoji: '🤔',
      title: 'Predict them',
      body: 'Everybody else picks what they think the answer was.',
    },
    {
      emoji: '🎯',
      title: 'Scores',
      body: '100 for each right prediction. The person in the spotlight scores for every friend who knew them.',
    },
  ],
  blindranking: [
    {
      emoji: '🙈',
      title: 'One at a time',
      body: 'Ten things come up one by one. Put each in a slot from 1 to 10 before you see the next.',
    },
    {
      emoji: '🔒',
      title: 'No take-backs',
      body: 'Once something is placed it stays. Leave room for whatever might come.',
    },
    {
      emoji: '📊',
      title: 'Read the room',
      body: 'Together, the closer your list is to the room’s average, the more you score.',
    },
  ],
  onestar: [
    {
      emoji: '🎬',
      title: 'A classic film',
      body: 'A much-loved film comes up.',
    },
    {
      emoji: '⭐',
      title: 'Trash it',
      body: 'Write the most savage one-line review you can.',
    },
    {
      emoji: '🗳️',
      title: 'Vote',
      body: 'The reviews come up with no names. Vote for the best one that is not yours: 100 a vote.',
    },
  ],
  wagerquiz: [
    {
      emoji: '💰',
      title: 'A thousand to start',
      body: 'Everybody starts with 1,000 points.',
    },
    {
      emoji: '🎲',
      title: 'Bet first',
      body: 'You see only the category, then bet up to half your points before the question appears.',
    },
    {
      emoji: '💰',
      title: 'All in',
      body: 'Right wins the bet, wrong loses it. In the last round you can bet everything.',
    },
  ],
  fakinit: [
    {
      emoji: '🕵️',
      title: 'One of you is faking',
      body: 'Everybody gets the same question except the Faker, who gets a different one.',
    },
    {
      emoji: '✋',
      title: 'Answer it',
      body: 'Answer honestly. The Faker answers theirs and tries to blend in.',
    },
    {
      emoji: '🗳️',
      title: 'Find the Faker',
      body: 'The real question and every answer come out. Argue, then vote. Escape as the Faker for 300.',
    },
  ],
  undercover: [
    {
      emoji: '🤫',
      title: 'A secret word',
      body: 'Everybody gets the same word except one, who gets a similar word and is not told.',
    },
    {
      emoji: '💬',
      title: 'One-word clues',
      body: 'Twice round the table, one word each about your word. Not too obvious, not too vague.',
    },
    {
      emoji: '🗳️',
      title: 'Vote them out',
      body: 'Vote for the Undercover. Caught, they get one guess at everybody else’s word.',
    },
  ],
  mole: [
    {
      emoji: '👥',
      title: 'A team quiz',
      body: 'Everybody answers together; the majority answer is the team’s, and every right one adds to the pot.',
    },
    {
      emoji: '🕳️',
      title: 'There is a Mole',
      body: 'One of you can see the answers, and is trying to steer the team wrong.',
    },
    {
      emoji: '🗳️',
      title: 'Find the Mole',
      body: 'At the end of the round vote for the Mole. Caught, the pot goes to those who found them; if not, the Mole takes it.',
    },
  ],
};
